/**
 * SelectWidget component.
 * @module components/manage/Widgets/SelectWidget
 */

import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { map } from 'lodash';
import { defineMessages, injectIntl } from 'react-intl';
import { FormFieldWrapper } from '@plone/volto/components';
import { getVocabulary, getVocabularyTokenTitle } from '@plone/volto/actions';
import { getPersonaGenere } from 'uniba-volto-persone/actions';
import { getBaseUrl } from '@plone/volto/helpers';
import {
    customSelectStyles,
    DropdownIndicator,
    ClearIndicator,
    Option,
    selectTheme,
    MenuList,
    MultiValueContainer,
} from '@plone/volto/components/manage/Widgets/SelectStyling';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';

const messages = defineMessages({
    select: {
        id: 'Select…',
        defaultMessage: 'Select…',
    },
});

/**
 * SelectWidget component class.
 * @function SelectGenereWidget
 * @returns {string} Markup of the component.
 */
class SelectGenereWidget extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedOption: null,
        };
    }
    /**
     * Property types.
     * @property {Object} propTypes Property types.
     * @static
     */
    static propTypes = {
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        required: PropTypes.bool,
        error: PropTypes.arrayOf(PropTypes.string),
        getVocabulary: PropTypes.func.isRequired,
        getVocabularyTokenTitle: PropTypes.func.isRequired,
        choices: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        ),
        items: PropTypes.shape({
            vocabulary: PropTypes.object,
        }),
        widgetOptions: PropTypes.shape({
            vocabulary: PropTypes.object,
        }),
        value: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.string,
            PropTypes.bool,
            PropTypes.func,
            PropTypes.array,
        ]),
        onChange: PropTypes.func.isRequired,
        onBlur: PropTypes.func,
        onClick: PropTypes.func,
        onEdit: PropTypes.func,
        onDelete: PropTypes.func,
        wrapped: PropTypes.bool,
        noValueOption: PropTypes.bool,
        customOptionStyling: PropTypes.any,
        isMulti: PropTypes.bool,
        placeholder: PropTypes.string,
    };

    /**
     * Default properties
     * @property {Object} defaultProps Default properties.
     * @static
     */
    static defaultProps = {
        description: null,
        required: false,
        items: {
            vocabulary: null,
        },
        widgetOptions: {
            vocabulary: null,
        },
        error: [],
        choices: [],
        value: null,
        onChange: () => { },
        onBlur: () => { },
        onClick: () => { },
        onEdit: null,
        onDelete: null,
        noValueOption: true,
        customOptionStyling: null,
    };

    /**
    * Component did mount
    * @method componentDidMount
    * @returns {undefined}
    */
    componentDidMount() {
        const { pathname, getPersonaGenere } = this.props;

        // Carico le options al caricamento del componente
        if (pathname) {
            getPersonaGenere(getBaseUrl(pathname));
        }
    }

    componentDidUpdate(prevProps) {
        const { pathname, getPersonaGenere } = this.props;
        const { selectedOption } = this.state;

        // Aggiorno le opzioni se cambia l'url
        if (prevProps.pathname !== pathname) {
            getPersonaGenere(getBaseUrl(pathname));

            // Aspetto il prossimo update
            return;
        }

        // Se le options non sono state ancora caricate, aspetto il prossimo update
        const options = this.getOptions(this.props);
        if (options?.length === 0) {
            return;
        }

        // Prendo l'opzione selezionata
        const selectedFromProps = this.getSelectedOption(options, this.props);

        // Se l'opzione selezionata trovata è cambiata da prima 
        // e prima non era selezionata, la imposto nello state
        if (selectedFromProps != selectedOption && !selectedOption) {
            this.handleChange(selectedFromProps);
        }
    }

    getOptions = (props) => {
        const options_result = props.genre_vocab.items[getBaseUrl(props.location.pathname)] || [];
        return options_result.map(option => ({
            value: option,
            token: option,
            label: option,
        }));
    };


    getSelectedOption = (options, props) => {
        const value = props.value;

        // Se il valore non è presente, prendo la prima options
        if (value === null) {
            return options[0];
        }

        // Cerco la options corrispondente, altrimenti la prima
        const token = value.token;
        return options.find(o => o.value === token) || options[0];
    };

    handleChange = (selectedOption) => {
        this.setState({ selectedOption });
        this.props.onChange(this.props.id, selectedOption);
    };

    /**
     * Render method.
     * @method render
     * @returns {string} Markup for the component.
     */
    render() {
        const { id } = this.props;
        const disabled = this.props.disabled || this.props.isDisabled;
        const Select = this.props.reactSelect.default;

        const options = this.getOptions(this.props) || [];
        const hasOptions = options?.length > 0;
        const selectedOption = this.state.selectedOption || null;

        return (
            <div key={"strutturaAfferenza_responsabile"}>
                <FormFieldWrapper {...this.props}>
                    <Select
                        id={`field-${id}`}
                        name={id}
                        menuShouldScrollIntoView={false}
                        isDisabled={disabled}
                        isSearchable={true}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isMulti={false}
                        options={options}
                        styles={customSelectStyles}
                        theme={selectTheme}
                        components={{
                            ...(options?.length > 25 && {
                                MenuList,
                            }),
                            MultiValueContainer,
                            DropdownIndicator,
                            ClearIndicator,
                            Option: this.props.customOptionStyling || Option,
                        }}
                        value={hasOptions ? selectedOption : null}
                        placeholder={
                            this.props.placeholder ??
                            this.props.intl.formatMessage(messages.select)
                        }
                        onChange={this.handleChange}
                    />
                </FormFieldWrapper>
            </div>
        );
    }
}

export const SelectGenereWidgetComponent = injectIntl(SelectGenereWidget);

export default compose(
    injectLazyLibs(['reactSelect']),
    withRouter,
    connect(
        (state, props) => ({
            lang: state.intl.locale,
            genre_vocab: state.personaGenereReducer,
            pathname: props.location.pathname
        }),
        (dispatch) =>
            bindActionCreators(
                {
                    getVocabulary,
                    getVocabularyTokenTitle,
                    getPersonaGenere,
                },
                dispatch
            )
    ),
)(SelectGenereWidgetComponent);
